import { createContext, useContext, useState, useEffect, useMemo, type ReactNode } from "react";
import { useGetSettings } from "@workspace/api-client-react";

// Two layers of pre-React-hydration data make the first price paint correct
// and flicker-free, in priority order:
//
//   1. `window.__INITIAL_SETTINGS__` — injected synchronously by the SSR shell
//      (artifacts/store/server.mjs). Always reflects the *current* tenant for
//      *this* request, so it is correct even on a brand-new device or in
//      incognito mode where no localStorage cache exists.
//   2. localStorage cache — last known good settings from the previous visit.
//      Used as a fallback when the SSR injection is missing (dev server, very
//      first request before the API came up, or a transient API timeout).
//
// Without either, the storefront would briefly render every price ex-VAT
// (because `useGetSettings()` returns undefined on the first React render)
// and then snap to the tax-inclusive value once the request resolves — the
// "106 → 110" flicker merchants kept reporting on the product detail page.
//
// Bumped to v2 when the multi-country tax system landed (countryCode, taxScheme,
// taxLabel/Ar). v1 caches are simply ignored — they refresh on the next render.
const TAX_SETTINGS_CACHE_KEY = "nx.taxSettings.v2";

type CachedTaxSettings = {
  pricesIncludeTax: boolean;
  taxRate: number;
  baseCurrency: string;
  // Optional: only present when the source actually provided a custom symbol.
  // When undefined, callers fall back to CURRENCY_SYMBOLS[baseCurrency].
  baseSymbol?: string;
  // Multi-country tax labels. Optional so older v1-shaped SSR payloads still
  // load — defaults preserve KSA / VAT 15% behaviour.
  taxLabel?: string;
  taxLabelAr?: string;
  taxScheme?: string;
  countryCode?: string;
};

function readSsrInjectedSettings(): CachedTaxSettings | null {
  if (typeof window === "undefined") return null;
  const raw = (window as any).__INITIAL_SETTINGS__;
  if (!raw || typeof raw !== "object") return null;
  if (typeof raw.taxRate !== "number") return null;
  return {
    pricesIncludeTax: Boolean(raw.pricesIncludeTax),
    taxRate: Number(raw.taxRate),
    baseCurrency: typeof raw.currency === "string" && raw.currency ? raw.currency : "SAR",
    baseSymbol: typeof raw.currencySymbol === "string" && raw.currencySymbol ? raw.currencySymbol : undefined,
    taxLabel: typeof raw.taxLabel === "string" ? raw.taxLabel : undefined,
    taxLabelAr: typeof raw.taxLabelAr === "string" ? raw.taxLabelAr : undefined,
    taxScheme: typeof raw.taxScheme === "string" ? raw.taxScheme : undefined,
    countryCode: typeof raw.countryCode === "string" ? raw.countryCode : undefined,
  };
}

function readCachedTaxSettings(): CachedTaxSettings | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(TAX_SETTINGS_CACHE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (typeof p?.pricesIncludeTax !== "boolean") return null;
    if (typeof p?.taxRate !== "number") return null;
    return {
      pricesIncludeTax: p.pricesIncludeTax,
      taxRate: p.taxRate,
      baseCurrency: p.baseCurrency || "SAR",
      baseSymbol: typeof p.baseSymbol === "string" && p.baseSymbol ? p.baseSymbol : undefined,
      taxLabel: typeof p.taxLabel === "string" ? p.taxLabel : undefined,
      taxLabelAr: typeof p.taxLabelAr === "string" ? p.taxLabelAr : undefined,
      taxScheme: typeof p.taxScheme === "string" ? p.taxScheme : undefined,
      countryCode: typeof p.countryCode === "string" ? p.countryCode : undefined,
    };
  } catch { return null; }
}
function writeCachedTaxSettings(s: CachedTaxSettings) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(TAX_SETTINGS_CACHE_KEY, JSON.stringify(s)); } catch { /* ignore quota errors */ }
}

const EXCHANGE_RATES: Record<string, number> = {
  SAR: 1,
  AED: 0.98,
  USD: 0.2667,
  EUR: 0.245,
  GBP: 0.212,
  KWD: 0.082,
  BHD: 0.1005,
  QAR: 0.971,
  OMR: 0.1027,
  EGP: 13.2,
};

const CURRENCY_SYMBOLS: Record<string, string> = {
  SAR: "﷼",
  AED: "د.إ",
  USD: "$",
  EUR: "€",
  GBP: "£",
  KWD: "د.ك",
  BHD: "د.ب",
  QAR: "ر.ق",
  OMR: "ر.ع",
  EGP: "ج.م",
};

export interface SupportedCurrency {
  code: string;
  symbol: string;
  nameAr: string;
  nameEn: string;
}

export const SUPPORTED_CURRENCIES: SupportedCurrency[] = [
  { code: "SAR", symbol: "﷼",   nameAr: "ريال سعودي",   nameEn: "Saudi Riyal" },
  { code: "AED", symbol: "د.إ", nameAr: "درهم إماراتي", nameEn: "UAE Dirham" },
  { code: "USD", symbol: "$",   nameAr: "دولار أمريكي", nameEn: "US Dollar" },
  { code: "EUR", symbol: "€",   nameAr: "يورو",         nameEn: "Euro" },
  { code: "GBP", symbol: "£",   nameAr: "جنيه إسترليني", nameEn: "British Pound" },
  { code: "KWD", symbol: "د.ك", nameAr: "دينار كويتي",  nameEn: "Kuwaiti Dinar" },
  { code: "BHD", symbol: "د.ب", nameAr: "دينار بحريني", nameEn: "Bahraini Dinar" },
  { code: "QAR", symbol: "ر.ق", nameAr: "ريال قطري",    nameEn: "Qatari Riyal" },
  { code: "OMR", symbol: "ر.ع", nameAr: "ريال عماني",   nameEn: "Omani Riyal" },
  { code: "EGP", symbol: "ج.م", nameAr: "جنيه مصري",    nameEn: "Egyptian Pound" },
];

export function getCurrencySymbol(code: string): string {
  return CURRENCY_SYMBOLS[code] || code;
}

// Legacy fallback. Real rate now comes from store settings via `useCurrency()`
// → `taxRatePct`. These helpers remain exported for the handful of callers
// that compute totals offline (e.g. exports), but new code MUST use the
// dynamic value from context so the multi-country tax system works.
export const TAX_RATE = 0.15;

export function addTax(amount: number, rate: number = TAX_RATE): number {
  return Math.round(amount * (1 + rate) * 100) / 100;
}

export function getTaxAmount(amount: number, rate: number = TAX_RATE): number {
  return Math.round(amount * rate * 100) / 100;
}

// Tax classes are seeded identically across every tenant; id 3 is always
// "Tax Exempt". Products carrying this class must skip the VAT multiplier
// in `formatPrice` so the price the customer sees on the card matches the
// price the cart actually charges (the backend mirrors this rule by
// excluding exempt items from the taxable base).
export const TAX_EXEMPT_CLASS_ID = 3;
export function isTaxExempt(taxClassId: number | null | undefined): boolean {
  return taxClassId != null && Number(taxClassId) === TAX_EXEMPT_CLASS_ID;
}

interface FormatPriceOptions {
  taxExempt?: boolean;
}

interface CurrencyContextType {
  currencySymbol: string;
  currencyCode: string;
  currency: string;
  setCurrency: (code: string) => void;
  formatPrice: (amount: number, options?: FormatPriceOptions) => string;
  formatPriceRaw: (amount: number) => string;
  // Multi-country tax system (T#43). Use these to render the
  // localized scheme label + rate everywhere instead of hard-coding
  // "VAT 15%". Defaults preserve KSA / VAT 15% behaviour for tenants
  // that haven't picked a country preset yet.
  taxRatePct: number;
  taxLabel: string;
  taxLabelAr: string;
  taxScheme: string;
  countryCode: string;
  pricesIncludeTax: boolean;
}

const CurrencyContext = createContext<CurrencyContextType>({
  currencySymbol: "﷼",
  currencyCode: "SAR",
  currency: "SAR",
  setCurrency: () => {},
  formatPrice: (amount) => `﷼ ${amount.toFixed(2)}`,
  formatPriceRaw: (amount) => `﷼ ${amount.toFixed(2)}`,
  taxRatePct: 15,
  taxLabel: "VAT",
  taxLabelAr: "ضريبة القيمة المضافة",
  taxScheme: "VAT",
  countryCode: "SA",
  pricesIncludeTax: false,
});

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const { data: settings } = useGetSettings();

  // Pre-React fallbacks evaluated ONCE on mount. The SSR injection takes
  // priority because it is guaranteed to match the current request's tenant;
  // the localStorage cache is the secondary fallback for environments where
  // SSR injection is unavailable (dev server, transient API timeout).
  const initialFallback = useMemo<CachedTaxSettings | null>(
    () => readSsrInjectedSettings() ?? readCachedTaxSettings(),
    [],
  );

  const baseCurrency = settings?.currency || initialFallback?.baseCurrency || "SAR";
  // Priority: live settings > SSR-injected/cached merchant override > built-in
  // symbol map > Saudi Riyal glyph as ultimate default. The fallback symbol is
  // critical when a merchant has set a custom currency_symbol in settings —
  // without it, the first paint would briefly show the built-in glyph and
  // then flip to the merchant's symbol once /api/settings resolves.
  const baseSymbol =
    settings?.currencySymbol ||
    initialFallback?.baseSymbol ||
    CURRENCY_SYMBOLS[baseCurrency] ||
    "﷼";
  // When the merchant has enabled "الأسعار تشمل الضريبة", every product price
  // shown on the storefront (cards, cart line items, subtotal) should appear
  // inclusive of VAT. Stored prices in the catalog stay ex-VAT, so we apply
  // the multiplier here at display time. Backend-computed totals (tax line,
  // total line, shipping) already arrive in their final form and must use
  // `formatPriceRaw` to avoid double-counting.
  // Fall back to the SSR-injected / localStorage-cached values while the live
  // /settings request is in flight to eliminate the inclusive-vs-exclusive
  // flicker on first paint.
  const pricesIncludeTax = settings != null
    ? Boolean(settings.pricesIncludeTax)
    : Boolean(initialFallback?.pricesIncludeTax);
  const taxRatePct = settings != null
    ? Number(settings.taxRate ?? 0)
    : Number(initialFallback?.taxRate ?? 0);
  const taxFactor = pricesIncludeTax && taxRatePct > 0 ? 1 + taxRatePct / 100 : 1;

  // Multi-country tax labels (KSA default kept for backwards-compat). The
  // admin Settings page can override these via the Country preset picker
  // or by typing custom labels directly.
  const taxLabel = (settings as any)?.taxLabel || initialFallback?.taxLabel || "VAT";
  const taxLabelAr = (settings as any)?.taxLabelAr || initialFallback?.taxLabelAr || "ضريبة القيمة المضافة";
  const taxScheme = (settings as any)?.taxScheme || initialFallback?.taxScheme || "VAT";
  const countryCode = (settings as any)?.countryCode || initialFallback?.countryCode || "SA";

  // Persist the freshly-fetched tax settings so the next page load can
  // render prices correctly without waiting for the /settings round-trip.
  useEffect(() => {
    if (settings == null) return;
    writeCachedTaxSettings({
      pricesIncludeTax: Boolean(settings.pricesIncludeTax),
      taxRate: Number(settings.taxRate ?? 0),
      baseCurrency: settings.currency || "SAR",
      baseSymbol: typeof settings.currencySymbol === "string" && settings.currencySymbol
        ? settings.currencySymbol
        : undefined,
      taxLabel: (settings as any).taxLabel || "VAT",
      taxLabelAr: (settings as any).taxLabelAr || "ضريبة القيمة المضافة",
      taxScheme: (settings as any).taxScheme || "VAT",
      countryCode: (settings as any).countryCode || "SA",
    });
  }, [
    settings?.pricesIncludeTax,
    settings?.taxRate,
    settings?.currency,
    settings?.currencySymbol,
    (settings as any)?.taxLabel,
    (settings as any)?.taxLabelAr,
    (settings as any)?.taxScheme,
    (settings as any)?.countryCode,
  ]);

  const [selectedCurrency, setSelectedCurrency] = useState(() => {
    if (typeof window === "undefined") return baseCurrency;
    return localStorage.getItem("selectedCurrency") || baseCurrency;
  });

  useEffect(() => {
    if (selectedCurrency && selectedCurrency !== baseCurrency) {
      localStorage.setItem("selectedCurrency", selectedCurrency);
    }
  }, [selectedCurrency]);

  const setCurrency = (code: string) => {
    setSelectedCurrency(code);
    localStorage.setItem("selectedCurrency", code);
  };

  const convertAmount = (amount: number) => {
    const val = amount ?? 0;
    if (selectedCurrency !== baseCurrency) {
      const baseRate = EXCHANGE_RATES[baseCurrency] || 1;
      const targetRate = EXCHANGE_RATES[selectedCurrency] || 1;
      return (val / baseRate) * targetRate;
    }
    return val;
  };

  const formatNum = (num: number) => {
    const symbol = CURRENCY_SYMBOLS[selectedCurrency] || baseSymbol;
    // Round to 2 decimals first so float-math residue (e.g. 412.17 * 1.15 =
    // 473.9954999…) doesn't force trailing ".00" on a value that is really a
    // whole riyal once tax-inclusive pricing is applied.
    const rounded = Math.round(num * 100) / 100;
    // `minimumFractionDigits: 0` drops trailing zeros so 400.00 → "400" and
    // 400.50 → "400.5" while 400.55 stays "400.55".
    const formatted = rounded.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    // Number first, symbol second. In RTL (Arabic) rendering the bidi
    // algorithm flips the visual order so the symbol ends up on the LEFT
    // and the number on the RIGHT — matching what merchants expect for
    // ر.س / ﷼. In LTR (English) rendering it reads as "87 SAR".
    return `${formatted} ${symbol}`;
  };

  // `formatPrice` is for catalog/cart values stored ex-VAT. It applies the
  // tax-inclusive multiplier when the merchant toggle is on so customers see
  // the final price they will pay on every product card and line item.
  // Pass `{ taxExempt: true }` for products marked as tax exempt so the
  // multiplier is skipped — the cart will not add VAT for these items.
  const formatPrice = (amount: number, options?: FormatPriceOptions) => {
    const factor = options?.taxExempt ? 1 : taxFactor;
    const converted = convertAmount((amount ?? 0) * factor);
    return formatNum(converted);
  };

  // `formatPriceRaw` is for backend-computed values (tax, shipping, total)
  // that are already in their final form and must NOT be multiplied again.
  const formatPriceRaw = (amount: number) => {
    const converted = convertAmount(amount ?? 0);
    return formatNum(converted);
  };

  return (
    <CurrencyContext.Provider value={{
      currencySymbol: CURRENCY_SYMBOLS[selectedCurrency] || baseSymbol,
      currencyCode: selectedCurrency,
      currency: selectedCurrency,
      setCurrency,
      formatPrice,
      formatPriceRaw,
      taxRatePct,
      taxLabel,
      taxLabelAr,
      taxScheme,
      countryCode,
      pricesIncludeTax,
    }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  return useContext(CurrencyContext);
}
